import React from 'react';
import type { IntlShape } from 'react-intl';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { QuickInsertHandlerFn } from '@atlaskit/editor-common/types/quick-insert';
import type { EditorCommand } from '@atlaskit/editor-common/types/editor-command';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert/quick-insert-plugin-type';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import TemplateIcon from '@atlaskit/icon-lab/core/template';

import type {
	QuickInsertItem,
	QuickInsertActionInsert,
} from '@atlaskit/editor-common/provider-factory/quick-insert-provider';

import { fg } from '@confluence/feature-gating';

import { ANALYTICS_ORIGIN } from '../constants/snippetAnalytics.ts';
import {
	buildInsertSnippetEventData,
	fireSnippetUIEvent,
} from '../hooks/analytics/snippetAnalyticsPayload.ts';
import type { Snippet } from '../types/snippet.ts';
import {
	createSnippetDescription,
	extractSnippetKeywords,
	getSnippetQuickInsertKey,
} from '../utils/quickInsertUtils.ts';

/**
 * Editor API shape used by the quick-insert snippet item.
 *
 * The analytics plugin is optional at runtime, so rather than forcing every
 * caller to provide an `AnalyticsPlugin`-typed API we keep the public shape as
 * the QuickInsert API and read the (optional) analytics plugin defensively.
 */
type SnippetQuickInsertEditorAPI = PublicPluginAPI<[QuickInsertPlugin]>;

/** The editor API as accepted by this module — may be absent at runtime. */
type OptionalSnippetQuickInsertEditorAPI = SnippetQuickInsertEditorAPI | null | undefined;

/**
 * Fires an `insertSnippet` UI event with `origin=quickInsert` using the
 * editor's analytics plugin. No-op if the analytics plugin or its
 * `createAnalyticsEvent` factory is unavailable.
 *
 * `PublicPluginAPI` exposes optional plugin dependencies (such as analytics)
 * as optional properties, so the analytics plugin can be read defensively via
 * optional chaining without a type assertion.
 */
const fireQuickInsertSnippetInsertedAnalytic = (
	editorAPI: OptionalSnippetQuickInsertEditorAPI,
	snippet: Snippet,
) => {
	if (!fg('platform_editor_blocks_patch_2')) {
		return;
	}
	const analyticsApi = editorAPI?.analytics;
	const createAnalyticsEvent = analyticsApi?.sharedState.currentState()?.createAnalyticsEvent;
	fireSnippetUIEvent(
		createAnalyticsEvent,
		buildInsertSnippetEventData(snippet),
		ANALYTICS_ORIGIN.QUICK_INSERT,
	);
};

/**
 * Adds a snippet to the quick insert menu using the editor API.
 */
export const addSnippetQuickInsertItem = ({
	snippet,
	editorAPI,
}: {
	snippet: Snippet;
	editorAPI: OptionalSnippetQuickInsertEditorAPI;
}) => {
	if (!editorAPI) {
		return;
	}
	const addQuickInsertItem: ((item: QuickInsertHandlerFn) => EditorCommand) | undefined =
		editorAPI?.quickInsert?.commands?.addQuickInsertItem;

	if (addQuickInsertItem === undefined) {
		return;
	}

	editorAPI.core?.actions.execute(
		addQuickInsertItem((intl: IntlShape) => [
			createSnippetQuickInsertItem({ snippet, intl, editorAPI }),
		]),
	);
};

/**
 * Updates an existing snippet in the editor's quick insert menu (e.g. after edit or share/unshare).
 * No-op if editorAPI is null/undefined or updateQuickInsertItem is not available.
 */
export const updateSnippetInQuickInsertItem = ({
	snippet,
	editorAPI,
}: {
	snippet: Snippet;
	editorAPI: OptionalSnippetQuickInsertEditorAPI;
}): void => {
	if (!editorAPI) {
		return;
	}
	const updateQuickInsertItem = editorAPI?.quickInsert?.commands?.updateQuickInsertItem ?? null;
	if (!updateQuickInsertItem) {
		return;
	}
	editorAPI.core?.actions.execute(
		updateQuickInsertItem(getSnippetQuickInsertKey(snippet.snippetId), (intl: IntlShape) => [
			createSnippetQuickInsertItem({ snippet, intl, editorAPI }),
		]),
	);
};

/**
 * Creates a snippet quick insert item.
 *
 * When `editorAPI` is provided (and exposes the analytics plugin), inserting a
 * snippet via quick-insert fires an `insertSnippet` UI event with
 * `origin=quickInsert`, ensuring the Confluence insert entry-point is captured
 * for the quick-insert / block-menu surface (not just the snippet panel).
 */
export const createSnippetQuickInsertItem = ({
	snippet,
	intl,
	editorAPI,
}: {
	snippet: Snippet;
	intl: IntlShape;
	editorAPI?: SnippetQuickInsertEditorAPI | null;
}): QuickInsertItem => {
	return {
		key: getSnippetQuickInsertKey(snippet.snippetId),
		title: snippet.name,
		description: createSnippetDescription(snippet.createdBy, intl, snippet.description),
		keywords: extractSnippetKeywords(snippet.name, intl),
		isDisabledOffline: true,
		icon: () => <TemplateIcon label="Snippet" />,
		action: (insert: QuickInsertActionInsert, state: EditorState) => {
			const body = snippet.body;
			if (!body) {
				return false;
			}

			const result = Array.isArray(body)
				? insert(Fragment.fromJSON(state.schema, body))
				: insert(body);

			if (result) {
				fireQuickInsertSnippetInsertedAnalytic(editorAPI, snippet);
			}

			return result;
		},
	};
};
